import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { videoService } from '../../services'
import { useAuthStore, useUiStore, useUploadStore } from '../../stores'
import type { Video } from '../../types/database.types'
import { Button, LoadingScreen, Modal, Spinner } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import { useVideoStatusSubscription } from '../../hooks'
import { muxConfig } from '../../config/mux.config'

type VideoStatusFilter = 'all' | 'ready' | 'processing' | 'uploading' | 'error'

export function MyVideosPage() {
  const { profile, canUploadVideos } = useAuthStore()
  const { openModal, closeModal, modals } = useUiStore()
  const { activeUploads, cancelUpload, removeUpload } = useUploadStore()
  const toast = useToast()

  const [videos, setVideos] = useState<Video[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<VideoStatusFilter>('all')
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Load videos on mount
  useEffect(() => {
    const loadVideos = async () => {
      if (!profile) return

      try {
        const data = await videoService.getMyVideos(profile.id)
        setVideos(data)
      } catch (error) {
        console.error('Failed to load videos:', error)
        toast.error('Failed to load your videos')
      } finally {
        setIsLoading(false)
      }
    }

    loadVideos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  // Real-time video status subscription
  const handleVideoUpdate = useCallback((updatedVideo: Video) => {
    setVideos((prev) => {
      const exists = prev.some((v) => v.id === updatedVideo.id)
      if (exists) {
        return prev.map((v) => (v.id === updatedVideo.id ? updatedVideo : v))
      }
      // New video inserted - add to beginning
      return [updatedVideo, ...prev]
    })
  }, [])

  const handleStatusChange = useCallback(
    ({
      videoId,
      newStatus,
      oldStatus,
      video,
    }: {
      videoId: string
      newStatus: string
      oldStatus: string | null
      video: Video
    }) => {
      // Show toast for significant status changes
      if (newStatus === 'ready' && oldStatus !== 'ready') {
        toast.success(`"${video.title}" is ready to view!`)
        // Remove from active uploads if it was there
        removeUpload(videoId)
      } else if (newStatus === 'error') {
        toast.error(`"${video.title}" failed to process`)
        removeUpload(videoId)
      }
    },
    [toast, removeUpload]
  )

  const handleVideoInsert = useCallback((newVideo: Video) => {
    setVideos((prev) => {
      const exists = prev.some((v) => v.id === newVideo.id)
      if (exists) return prev
      return [newVideo, ...prev]
    })
  }, [])

  useVideoStatusSubscription({
    userId: profile?.id ?? '',
    onVideoUpdate: handleVideoUpdate,
    onStatusChange: handleStatusChange,
    onVideoInsert: handleVideoInsert,
    enabled: !!profile,
  })

  const filteredVideos =
    statusFilter === 'all'
      ? videos
      : videos.filter((v) => v.status === statusFilter)

  const handleDeleteClick = (video: Video) => {
    setVideoToDelete(video)
    openModal('delete-video')
  }

  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return

    setIsDeleting(true)
    try {
      await videoService.deleteVideo(videoToDelete.id)
      setVideos((prev) => prev.filter((v) => v.id !== videoToDelete.id))
      toast.success('Video deleted successfully')
      closeModal('delete-video')
    } catch (error) {
      console.error('Failed to delete video:', error)
      toast.error('Failed to delete video')
    } finally {
      setIsDeleting(false)
      setVideoToDelete(null)
    }
  }

  // Handle cancel upload
  const handleCancelUpload = (videoId: string) => {
    cancelUpload(videoId)
    setVideos((prev) => prev.filter((v) => v.id !== videoId))
    toast.success('Upload cancelled')
  }

  // Get status badge with animations and progress
  const getStatusBadge = (video: Video) => {
    const upload = activeUploads[video.id]

    // If there's an active upload for this video, show progress
    if (upload && upload.phase === 'uploading') {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
              <svg
                className="w-3 h-3 animate-pulse"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {upload.progress}%
            </span>
            <button
              onClick={() => handleCancelUpload(video.id)}
              className="text-xs text-red-600 hover:text-red-800 font-medium"
            >
              Cancel
            </button>
          </div>
          <div className="w-24 bg-blue-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${upload.progress}%` }}
            />
          </div>
        </div>
      )
    }

    // If upload is finalizing
    if (upload && upload.phase === 'finalizing') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
          <Spinner size="sm" />
          Saving...
        </span>
      )
    }

    // If upload had an error
    if (upload && upload.phase === 'error') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {upload.errorMessage || 'Upload failed'}
        </span>
      )
    }

    // Standard status badges
    switch (video.status) {
      case 'ready':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Ready
          </span>
        )
      case 'processing':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing
          </span>
        )
      case 'uploading':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Uploading
          </span>
        )
      case 'error':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Error
          </span>
        )
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
            Unknown
          </span>
        )
    }
  }

  const getVisibilityBadge = (visibility: string | null) => {
    switch (visibility) {
      case 'public':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
            Public
          </span>
        )
      case 'organization':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
            Org Only
          </span>
        )
      case 'private':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
            Private
          </span>
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Loading your videos..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Videos</h1>
          <p className="mt-1 text-gray-600">
            Manage and organize your exercise videos.
          </p>
        </div>
        {canUploadVideos() && (
          <Link to="/dashboard/videos/upload">
            <Button>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Upload Video
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600">Filter by status:</span>
        <div className="flex gap-2">
          {(['all', 'ready', 'processing', 'uploading', 'error'] as const).map(
            (status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            )
          )}
        </div>
      </div>

      {/* Videos list */}
      {filteredVideos.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          {videos.length === 0 ? (
            <>
              <svg
                className="w-12 h-12 text-gray-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No videos yet
              </h3>
              <p className="text-gray-600 mb-4">
                Upload your first exercise video to get started.
              </p>
              {canUploadVideos() && (
                <Link to="/dashboard/videos/upload">
                  <Button>Upload Your First Video</Button>
                </Link>
              )}
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No videos match this filter
              </h3>
              <p className="text-gray-600">
                Try selecting a different status filter.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Video
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visibility
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVideos.map((video) => (
                <tr key={video.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      {video.status === 'ready' && video.mux_playback_id ? (
                        <Link
                          to={`/videos/${video.id}`}
                          className="w-24 h-14 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                        >
                          <img
                            src={muxConfig.getThumbnailUrl(video.mux_playback_id, {
                              width: 192,
                              height: 112,
                              time: video.thumbnail_time ?? (video.duration_seconds ? video.duration_seconds * 0.25 : 0),
                            })}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        </Link>
                      ) : (
                        <div className="w-24 h-14 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                          <div className="w-full h-full flex items-center justify-center">
                            <svg
                              className="w-8 h-8 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                      <div className="min-w-0">
                        {video.status === 'ready' ? (
                          <Link
                            to={`/videos/${video.id}`}
                            className="font-medium text-gray-900 truncate hover:text-blue-600 transition-colors"
                          >
                            {video.title}
                          </Link>
                        ) : (
                          <p className="font-medium text-gray-900 truncate">
                            {video.title}
                          </p>
                        )}
                        {video.category && (
                          <p className="text-sm text-gray-500">
                            {video.category}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(video)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getVisibilityBadge(video.visibility)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        {video.view_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                          />
                        </svg>
                        {video.like_count || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(video.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {video.status === 'ready' && (
                        <Link
                          to={`/videos/${video.id}`}
                          className="text-gray-600 hover:text-gray-900"
                          title="View"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </Link>
                      )}
                      <Link
                        to={`/dashboard/videos/${video.id}/edit`}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(video)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={modals.includes('delete-video')}
        onClose={() => {
          closeModal('delete-video')
          setVideoToDelete(null)
        }}
        title="Delete Video"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{videoToDelete?.title}"? This
            action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                closeModal('delete-video')
                setVideoToDelete(null)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? <Spinner size="sm" /> : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
